/**
 * Team Service
 * Manages team members, roles, and permissions
 */

import type {
  TeamMember,
  CreateTeamMemberRequest,
  UpdateTeamMemberRequest,
  TeamMemberFilters,
  TeamStats,
  ActivityLog,
  PasswordResetRequest,
  UserSession,
  UserStatus,
} from '../../types/team';

import { isBackendUserDbEnabled } from '../backendMode';
import {
  type BackendTeamUserRow,
  type PatchTeamUserPayload,
  createTeamUser,
  deleteTeamUser,
  listTeamUsers,
  patchTeamUser,
  resendTeamInvite,
  setTeamUserPassword,
} from './customerTeamApi';

export class TeamService {
  // In-memory storage (replace with database in production)
  private members: Map<string, TeamMember> = new Map();
  private activityLogs: Map<string, ActivityLog[]> = new Map();
  private sessions: Map<string, UserSession> = new Map();
  private employeeCounter = 1001;
  private backendBaseUrl = (import.meta.env.VITE_BACKEND_API_URL || import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
  private readonly storageKey = 'coremonit_team_members_v1';
  private readonly activityStorageKey = 'coremonit_team_activity_v1';

  /** Set after successful API create (password / invite message). */
  lastCreateApiMeta: {
    temporaryPassword?: string;
    message?: string;
    /** True only when backend actually sent SMTP (not mock print). */
    invitationSent?: boolean;
  } | null = null;

  constructor() {
    if (isBackendUserDbEnabled()) {
      void this.syncFromBackend().catch(() => undefined);
      return;
    }
    this.loadState();
    void this.hydrateFromBackend().catch(() => undefined);
    if (this.members.size === 0) {
      this.initializeDemoData();
    }
  }

  /**
   * Load org users from Platform API (JWT). No-op when not using backend user DB.
   */
  async syncFromBackend(): Promise<void> {
    if (!isBackendUserDbEnabled()) return;
    const rows = await listTeamUsers();
    this.members = new Map(
      rows.map((row, i) => {
        const m = this.mapBackendRowToMember(row, i);
        return [m.id, m] as const;
      })
    );
  }

  /**
   * Create team member
   */
  async createMember(request: CreateTeamMemberRequest, createdBy: string = 'system'): Promise<TeamMember> {
    if (isBackendUserDbEnabled()) {
      this.lastCreateApiMeta = null;
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('coremonit_customer_token') : null;
      if (!token) throw new Error('Sign in required to manage team members.');
      const name = `${request.firstName} ${request.lastName}`.trim() || request.email.trim();
      const data = await createTeamUser({
        email: request.email.trim(),
        name,
        role_id: request.roleId,
        phone_number: request.phoneNumber,
        employee_id: request.employeeId,
        department: request.department,
        job_title: request.jobTitle,
        first_name: request.firstName,
        last_name: request.lastName,
        send_invitation: request.sendInvitation,
        password: request.password,
        auto_generate_password: request.autoGeneratePassword,
        access_type: request.accessType,
        access_start_date: request.accessStartDate,
        access_end_date: request.accessEndDate,
      });
      const newUserId = typeof data.user_id === 'string' ? data.user_id : undefined;
      this.lastCreateApiMeta = {
        temporaryPassword: typeof data.temporary_password === 'string' ? data.temporary_password : undefined,
        message: typeof data.message === 'string' ? data.message : undefined,
        invitationSent: data.invitation_sent === true,
      };
      await this.syncFromBackend();
      let m = newUserId ? this.members.get(newUserId) : undefined;
      if (!m) {
        const want = request.email.trim().toLowerCase();
        m = Array.from(this.members.values()).find((x) => x.email.trim().toLowerCase() === want);
      }
      if (!m && newUserId) {
        m = this.buildSyntheticMemberFromCreate(request, newUserId, data);
        this.members.set(m.id, m);
      }
      if (!m) throw new Error('Member created but could not reload the team list.');
      await this.logActivity({
        userId: createdBy,
        userName: 'Admin',
        action: `Created team member: ${m.firstName} ${m.lastName}`,
        actionType: 'create',
        module: 'team',
        resourceId: m.id,
        resourceType: 'team_member',
      });
      return m;
    }

    // Validate email uniqueness
    const emailExists = Array.from(this.members.values()).some(
      m => m.email.toLowerCase() === request.email.toLowerCase()
    );
    if (emailExists) {
      throw new Error('Email already exists');
    }

    // Validate employee ID uniqueness
    const employeeIdExists = Array.from(this.members.values()).some(
      m => m.employeeId === request.employeeId
    );
    if (employeeIdExists) {
      throw new Error('Employee ID already exists');
    }

    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    const member: TeamMember = {
      id,
      employeeId: request.employeeId,
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email,
      phoneNumber: request.phoneNumber,
      department: request.department,
      jobTitle: request.jobTitle,
      
      roleId: request.roleId,
      roleName: this.getRoleName(request.roleId),
      accessType: request.accessType,
      accessStartDate: request.accessStartDate,
      accessEndDate: request.accessEndDate,
      status: 'pending',
      
      requirePasswordReset: request.autoGeneratePassword,
      require2FA: false,
      
      loginCount: 0,
      createdAt: now,
      updatedAt: now,
      createdBy,
      invitationSent: request.sendInvitation,
      invitationAccepted: false,
    };

    this.members.set(id, member);
    this.persistState();

    // Log activity
    await this.logActivity({
      userId: createdBy,
      userName: 'Admin',
      action: `Created team member: ${member.firstName} ${member.lastName}`,
      actionType: 'create',
      module: 'team',
      resourceId: id,
      resourceType: 'team_member',
    });

    // Send invitation email (simulated)
    if (request.sendInvitation) {
      console.log(`📧 Invitation email sent to ${member.email}`);
    }

    console.log(`✅ Team member created: ${member.firstName} ${member.lastName} (${member.employeeId})`);
    return member;
  }

  /**
   * Update team member
   */
  async updateMember(id: string, updates: UpdateTeamMemberRequest, updatedBy: string = 'system'): Promise<TeamMember> {
    if (isBackendUserDbEnabled()) {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('coremonit_customer_token') : null;
      if (!token) throw new Error('Sign in required to manage team members.');
      const cur = this.members.get(id);
      if (!cur) throw new Error('Team member not found');

      const patch: PatchTeamUserPayload = {};
      if (updates.firstName !== undefined || updates.lastName !== undefined) {
        const fn = updates.firstName ?? cur.firstName;
        const ln = updates.lastName ?? cur.lastName;
        patch.name = `${fn} ${ln}`.trim();
      }
      if (updates.phoneNumber !== undefined) patch.phone_number = updates.phoneNumber;
      if (updates.department !== undefined) patch.department = updates.department;
      if (updates.jobTitle !== undefined) patch.job_title = updates.jobTitle;
      if (updates.roleId !== undefined) patch.role = this.wizardRoleToBackend(updates.roleId);
      if (updates.status !== undefined) {
        if (updates.status === 'blocked') patch.account_status = 'blocked';
        else if (updates.status === 'active') {
          patch.account_status = 'active';
          patch.blocked_until = null;
        } else if (updates.status === 'pending') patch.account_status = 'invited';
        else if (updates.status === 'inactive') patch.account_status = 'suspended';
      }

      if (Object.keys(patch).length > 0) {
        await patchTeamUser(id, patch);
      }
      await this.syncFromBackend();
      const updated = this.members.get(id);
      if (!updated) throw new Error('Team member not found');
      await this.logActivity({
        userId: updatedBy,
        userName: 'Admin',
        action: `Updated team member: ${updated.firstName} ${updated.lastName}`,
        actionType: 'update',
        module: 'team',
        resourceId: id,
        resourceType: 'team_member',
        details: { before: cur, after: updated },
      });
      return updated;
    }

    const member = this.members.get(id);
    if (!member) {
      throw new Error('Team member not found');
    }

    const before = { ...member };

    // Apply updates
    if (updates.firstName) member.firstName = updates.firstName;
    if (updates.lastName) member.lastName = updates.lastName;
    if (updates.phoneNumber !== undefined) member.phoneNumber = updates.phoneNumber;
    if (updates.department) member.department = updates.department;
    if (updates.jobTitle) member.jobTitle = updates.jobTitle;
    if (updates.roleId) {
      member.roleId = updates.roleId;
      member.roleName = this.getRoleName(updates.roleId);
    }
    if (updates.additionalPermissions) member.additionalPermissions = updates.additionalPermissions;
    if (updates.status) member.status = updates.status;
    if (updates.requirePasswordReset !== undefined) member.requirePasswordReset = updates.requirePasswordReset;
    if (updates.require2FA !== undefined) member.require2FA = updates.require2FA;

    member.updatedAt = new Date().toISOString();
    this.members.set(id, member);
    this.persistState();

    // Log activity
    await this.logActivity({
      userId: updatedBy,
      userName: 'Admin',
      action: `Updated team member: ${member.firstName} ${member.lastName}`,
      actionType: 'update',
      module: 'team',
      resourceId: id,
      resourceType: 'team_member',
      details: { before, after: member },
    });

    return member;
  }

  /**
   * Get team member by ID
   */
  getMember(id: string): TeamMember | undefined {
    return this.members.get(id);
  }

  /**
   * Get all team members
   */
  getMembers(filters?: TeamMemberFilters): TeamMember[] {
    let members = Array.from(this.members.values());

    if (!filters) {
      return members.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }

    // Apply filters
    if (filters.status?.length) {
      members = members.filter(m => filters.status!.includes(m.status));
    }

    if (filters.roleId?.length) {
      members = members.filter(m => filters.roleId!.includes(m.roleId));
    }

    if (filters.department?.length) {
      members = members.filter(m => filters.department!.includes(m.department));
    }

    if (filters.accessType?.length) {
      members = members.filter(m => filters.accessType!.includes(m.accessType));
    }

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      members = members.filter(m =>
        m.firstName.toLowerCase().includes(term) ||
        m.lastName.toLowerCase().includes(term) ||
        m.email.toLowerCase().includes(term) ||
        m.employeeId.toLowerCase().includes(term) ||
        m.department.toLowerCase().includes(term) ||
        m.jobTitle.toLowerCase().includes(term)
      );
    }

    return members.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /**
   * Block user
   */
  async blockMember(id: string, blockedBy: string = 'system'): Promise<void> {
    if (isBackendUserDbEnabled()) {
      await patchTeamUser(id, { account_status: 'blocked' });
      await this.syncFromBackend();
      const member = this.members.get(id);
      if (member) {
        await this.logActivity({
          userId: blockedBy,
          userName: 'Admin',
          action: `Blocked user: ${member.firstName} ${member.lastName}`,
          actionType: 'user_block',
          module: 'team',
          resourceId: id,
          resourceType: 'team_member',
        });
      }
      return;
    }

    const member = this.members.get(id);
    if (!member) {
      throw new Error('Team member not found');
    }

    member.status = 'blocked';
    member.updatedAt = new Date().toISOString();
    this.members.set(id, member);
    this.persistState();

    await this.logActivity({
      userId: blockedBy,
      userName: 'Admin',
      action: `Blocked user: ${member.firstName} ${member.lastName}`,
      actionType: 'user_block',
      module: 'team',
      resourceId: id,
      resourceType: 'team_member',
    });

    console.log(`🚫 User blocked: ${member.email}`);
  }

  /**
   * Unblock user
   */
  async unblockMember(id: string, unblockedBy: string = 'system'): Promise<void> {
    if (isBackendUserDbEnabled()) {
      await patchTeamUser(id, { account_status: 'active', blocked_until: null });
      await this.syncFromBackend();
      const member = this.members.get(id);
      if (member) {
        await this.logActivity({
          userId: unblockedBy,
          userName: 'Admin',
          action: `Unblocked user: ${member.firstName} ${member.lastName}`,
          actionType: 'user_unblock',
          module: 'team',
          resourceId: id,
          resourceType: 'team_member',
        });
      }
      return;
    }

    const member = this.members.get(id);
    if (!member) {
      throw new Error('Team member not found');
    }

    member.status = 'active';
    member.updatedAt = new Date().toISOString();
    this.members.set(id, member);
    this.persistState();

    await this.logActivity({
      userId: unblockedBy,
      userName: 'Admin',
      action: `Unblocked user: ${member.firstName} ${member.lastName}`,
      actionType: 'user_unblock',
      module: 'team',
      resourceId: id,
      resourceType: 'team_member',
    });

    console.log(`✅ User unblocked: ${member.email}`);
  }

  /**
   * Delete member
   */
  async deleteMember(id: string, deletedBy: string = 'system'): Promise<void> {
    if (isBackendUserDbEnabled()) {
      const member = this.members.get(id);
      if (!member) throw new Error('Team member not found');
      await deleteTeamUser(id);
      await this.syncFromBackend();
      await this.logActivity({
        userId: deletedBy,
        userName: 'Admin',
        action: `Deleted team member: ${member.firstName} ${member.lastName}`,
        actionType: 'delete',
        module: 'team',
        resourceId: id,
        resourceType: 'team_member',
      });
      return;
    }

    const member = this.members.get(id);
    if (!member) {
      throw new Error('Team member not found');
    }

    this.members.delete(id);
    this.persistState();

    await this.logActivity({
      userId: deletedBy,
      userName: 'Admin',
      action: `Deleted team member: ${member.firstName} ${member.lastName}`,
      actionType: 'delete',
      module: 'team',
      resourceId: id,
      resourceType: 'team_member',
    });

    console.log(`🗑️ Team member deleted: ${member.email}`);
  }

  /**
   * Reset password
   */
  async resetPassword(request: PasswordResetRequest, resetBy: string = 'system'): Promise<void> {
    if (isBackendUserDbEnabled()) {
      const member = this.members.get(request.userId);
      if (!member) throw new Error('Team member not found');
      const pwd = request.temporaryPassword?.trim();
      if (pwd && pwd.length >= 8) {
        await setTeamUserPassword(request.userId, pwd);
        await this.syncFromBackend();
        await this.logActivity({
          userId: resetBy,
          userName: 'Admin',
          action: `Admin set password for: ${member.firstName} ${member.lastName}`,
          actionType: 'password_reset',
          module: 'team',
          resourceId: request.userId,
          resourceType: 'team_member',
        });
        return;
      }
      throw new Error('Set a new password (min 8 characters) in the Security tab to update this user.');
    }

    const member = this.members.get(request.userId);
    if (!member) {
      throw new Error('Team member not found');
    }

    member.requirePasswordReset = request.requireChange;
    member.lastPasswordChange = new Date().toISOString();
    member.updatedAt = new Date().toISOString();
    this.members.set(request.userId, member);
    this.persistState();

    if (request.sendEmail) {
      console.log(`📧 Password reset email sent to ${member.email}`);
    }

    await this.logActivity({
      userId: resetBy,
      userName: 'Admin',
      action: `Reset password for: ${member.firstName} ${member.lastName}`,
      actionType: 'password_reset',
      module: 'team',
      resourceId: request.userId,
      resourceType: 'team_member',
    });

    console.log(`🔑 Password reset for: ${member.email}`);
  }

  /**
   * Get team statistics
   */
  getStats(): TeamStats {
    const members = Array.from(this.members.values());
    const now = new Date();
    const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    // Count by department
    const departmentCounts = new Map<string, number>();
    members.forEach(m => {
      departmentCounts.set(m.department, (departmentCounts.get(m.department) || 0) + 1);
    });

    // Count by role
    const roleCounts = new Map<string, { roleId: string; roleName: string; count: number }>();
    members.forEach(m => {
      const existing = roleCounts.get(m.roleId);
      if (existing) {
        existing.count++;
      } else {
        roleCounts.set(m.roleId, { roleId: m.roleId, roleName: m.roleName, count: 1 });
      }
    });

    return {
      totalMembers: members.length,
      activeMembers: members.filter(m => m.status === 'active').length,
      inactiveMembers: members.filter(m => m.status === 'inactive').length,
      blockedMembers: members.filter(m => m.status === 'blocked').length,
      pendingInvitations: members.filter(m => m.status === 'pending').length,
      
      byDepartment: Array.from(departmentCounts.entries()).map(([department, count]) => ({
        department,
        count,
      })),
      
      byRole: Array.from(roleCounts.values()),
      
      activeToday: members.filter(m => {
        if (!m.lastActivity) return false;
        const lastActivity = new Date(m.lastActivity);
        return lastActivity.toDateString() === now.toDateString();
      }).length,
      
      activeThisWeek: members.filter(m => {
        if (!m.lastActivity) return false;
        const lastActivity = new Date(m.lastActivity);
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return lastActivity >= weekAgo;
      }).length,
      
      activeThisMonth: members.filter(m => {
        if (!m.lastActivity) return false;
        const lastActivity = new Date(m.lastActivity);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return lastActivity >= monthAgo;
      }).length,
      
      temporaryAccess: members.filter(m => m.accessType === 'temporary').length,
      
      expiringAccess: members.filter(m => {
        if (m.accessType !== 'temporary' || !m.accessEndDate) return false;
        const endDate = new Date(m.accessEndDate);
        return endDate >= now && endDate <= sevenDaysLater;
      }).length,
    };
  }

  /**
   * Get activity logs for a user
   */
  getActivityLogs(userId: string): ActivityLog[] {
    return this.activityLogs.get(userId) || [];
  }

  /**
   * Get all team activity logs (admin timeline)
   */
  getAllActivityLogs(): ActivityLog[] {
    const all: ActivityLog[] = [];
    this.activityLogs.forEach((items) => {
      all.push(...items);
    });
    return all.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  /**
   * Log activity
   */
  private async logActivity(log: Omit<ActivityLog, 'id' | 'timestamp'>): Promise<void> {
    const activityLog: ActivityLog = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      ...log,
    };

    const userLogs = this.activityLogs.get(log.userId) || [];
    userLogs.unshift(activityLog);
    this.activityLogs.set(log.userId, userLogs);
    this.persistState();
  }

  private loadState(): void {
    if (isBackendUserDbEnabled()) return;
    try {
      const membersRaw = localStorage.getItem(this.storageKey);
      if (membersRaw) {
        const parsed = JSON.parse(membersRaw) as TeamMember[];
        this.members = new Map(parsed.map((member) => [member.id, member]));
      }

      const activityRaw = localStorage.getItem(this.activityStorageKey);
      if (activityRaw) {
        const parsed = JSON.parse(activityRaw) as Record<string, ActivityLog[]>;
        this.activityLogs = new Map(Object.entries(parsed));
      }
    } catch (error) {
      console.error('Failed to load team state:', error);
    }
  }

  private persistState(): void {
    if (isBackendUserDbEnabled()) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.members.values())));
      localStorage.setItem(this.activityStorageKey, JSON.stringify(Object.fromEntries(this.activityLogs.entries())));
    } catch (error) {
      console.error('Failed to persist team state:', error);
    }
  }

  private async hydrateFromBackend(): Promise<void> {
    if (!this.backendBaseUrl) {
      return;
    }

    try {
      const response = await fetch(`${this.backendBaseUrl}/api/team/users`, { credentials: 'include' });
      if (!response.ok) {
        return;
      }

      const payload = await response.json();
      const rows = Array.isArray(payload) ? payload : payload?.data;
      if (!Array.isArray(rows) || rows.length === 0) {
        return;
      }

      const normalized = rows.map((row: any, index: number): TeamMember => ({
        id: String(row.id || crypto.randomUUID()),
        employeeId: String(row.employee_id || `EMP${1000 + index}`),
        firstName: String(row.first_name || row.name?.split(' ')?.[0] || 'User'),
        lastName: String(row.last_name || row.name?.split(' ')?.slice(1).join(' ') || 'Member'),
        email: String(row.email || `user${index}@coremonit.local`),
        phoneNumber: row.phone || row.phone_number,
        department: String(row.department || 'Engineering'),
        jobTitle: String(row.job_title || 'Engineer'),
        roleId: String(row.role_id || 'role-viewer'),
        roleName: this.getRoleName(String(row.role_id || 'role-viewer')),
        accessType: row.access_type || 'permanent',
        status: row.status || 'active',
        requirePasswordReset: false,
        require2FA: false,
        loginCount: Number(row.login_count || 0),
        createdAt: row.created_at || new Date().toISOString(),
        updatedAt: row.updated_at || new Date().toISOString(),
        createdBy: row.created_by || 'system',
        invitationSent: false,
        invitationAccepted: true,
      }));

      this.members = new Map(normalized.map((member) => [member.id, member]));
      this.persistState();
    } catch (error) {
      console.warn('Team backend hydration failed, using local state.', error);
    }
  }

  /**
   * When GET /team/users omits the new row (timing/cache) but POST returned user_id, build a row so the UI can proceed.
   */
  private buildSyntheticMemberFromCreate(
    request: CreateTeamMemberRequest,
    userId: string,
    data: Record<string, unknown>
  ): TeamMember {
    const roleId = request.roleId || 'role-viewer';
    const accountStatus =
      typeof data.account_status === 'string' ? data.account_status : request.sendInvitation ? 'invited' : 'active';
    const st = this.accountStatusToUserStatus(accountStatus);
    const now = new Date().toISOString();
    return {
      id: userId,
      employeeId: request.employeeId || `EMP${userId.replace(/-/g, '').slice(0, 8)}`,
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email.trim(),
      phoneNumber: request.phoneNumber,
      department: request.department || '',
      jobTitle: request.jobTitle || '',
      roleId,
      roleName: this.getRoleName(roleId),
      accessType: request.accessType,
      accessStartDate: request.accessStartDate,
      accessEndDate: request.accessEndDate,
      status: st,
      requirePasswordReset: false,
      require2FA: false,
      loginCount: 0,
      createdAt: now,
      updatedAt: now,
      createdBy: 'system',
      invitationSent: request.sendInvitation,
      invitationAccepted: !request.sendInvitation,
    };
  }

  private mapBackendRowToMember(row: BackendTeamUserRow, index: number): TeamMember {
    const roleStr = String(row.role || 'viewer');
    const roleId = this.backendRoleToWizardId(roleStr);
    const rawName = String(row.name || '').trim();
    const fn =
      (typeof row.first_name === 'string' && row.first_name.trim()) ||
      rawName.split(/\s+/)[0] ||
      'User';
    const ln =
      (typeof row.last_name === 'string' && row.last_name.trim()) ||
      rawName.split(/\s+/).slice(1).join(' ') ||
      'Member';
    const st = this.accountStatusToUserStatus(String(row.account_status || 'active'));
    const now = new Date().toISOString();
    const emailRaw = row.email != null ? String(row.email).trim() : '';
    return {
      id: String(row.id),
      employeeId: String(row.employee_id || `EMP${1000 + index}`),
      firstName: fn,
      lastName: ln,
      email: emailRaw,
      phoneNumber: row.mobile ? String(row.mobile) : undefined,
      department: String(row.department || ''),
      jobTitle: String(row.job_title || ''),
      roleId,
      roleName: this.getRoleName(roleId),
      accessType: row.access_until ? 'temporary' : 'permanent',
      accessEndDate: row.access_until ? String(row.access_until) : undefined,
      status: st,
      requirePasswordReset: false,
      require2FA: false,
      loginCount: 0,
      createdAt: row.created_at ? String(row.created_at) : now,
      updatedAt: row.updated_at ? String(row.updated_at) : now,
      createdBy: 'system',
      invitationSent: st === 'pending',
      invitationAccepted: st !== 'pending',
    };
  }

  private accountStatusToUserStatus(s: string): UserStatus {
    const x = (s || 'active').toLowerCase();
    if (x === 'invited') return 'pending';
    if (x === 'blocked') return 'blocked';
    if (x === 'suspended') return 'inactive';
    return 'active';
  }

  private backendRoleToWizardId(role: string): string {
    const m: Record<string, string> = {
      customer_admin: 'role-admin',
      tenant_owner: 'role-admin',
      devops: 'role-devops-engineer',
      member: 'role-developer',
      viewer: 'role-viewer',
      billing_manager: 'role-manager',
    };
    return m[role] || 'role-viewer';
  }

  private wizardRoleToBackend(roleId: string): string {
    const m: Record<string, string> = {
      'role-admin': 'customer_admin',
      'role-devops-engineer': 'devops',
      'role-developer': 'member',
      'role-manager': 'member',
      'role-viewer': 'viewer',
    };
    return m[roleId] || 'viewer';
  }

  /**
   * Resend invitation email (backend only).
   */
  async resendInvite(userId: string): Promise<void> {
    if (!isBackendUserDbEnabled()) return;
    await resendTeamInvite(userId);
    await this.syncFromBackend();
  }

  /**
   * Get role name by ID
   */
  private getRoleName(roleId: string): string {
    const roleNames: Record<string, string> = {
      'role-admin': 'Admin',
      'role-devops': 'DevOps Engineer',
      'role-developer': 'Developer',
      'role-viewer': 'Viewer',
      'role-manager': 'Manager',
    };
    return roleNames[roleId] || 'Unknown';
  }

  /**
   * Initialize demo data
   */
  private initializeDemoData(): void {
    const demoMembers: CreateTeamMemberRequest[] = [
      {
        firstName: 'John',
        lastName: 'Smith',
        employeeId: 'EMP1001',
        email: 'john.smith@coremonit.com',
        phoneNumber: '+1 (903) 961-6263',
        department: 'Engineering',
        jobTitle: 'Senior DevOps Engineer',
        roleId: 'role-devops',
        accessType: 'permanent',
        sendInvitation: false,
        autoGeneratePassword: false,
      },
      {
        firstName: 'Sarah',
        lastName: 'Johnson',
        employeeId: 'EMP1002',
        email: 'sarah.johnson@coremonit.com',
        phoneNumber: '+1 (555) 234-5678',
        department: 'Engineering',
        jobTitle: 'Full Stack Developer',
        roleId: 'role-developer',
        accessType: 'permanent',
        sendInvitation: false,
        autoGeneratePassword: false,
      },
      {
        firstName: 'Michael',
        lastName: 'Chen',
        employeeId: 'EMP1003',
        email: 'michael.chen@coremonit.com',
        phoneNumber: '+1 (555) 345-6789',
        department: 'Operations',
        jobTitle: 'Operations Manager',
        roleId: 'role-manager',
        accessType: 'permanent',
        sendInvitation: false,
        autoGeneratePassword: false,
      },
      {
        firstName: 'Emma',
        lastName: 'Davis',
        employeeId: 'EMP1004',
        email: 'emma.davis@coremonit.com',
        phoneNumber: '+1 (555) 456-7890',
        department: 'Support',
        jobTitle: 'Support Specialist',
        roleId: 'role-viewer',
        accessType: 'permanent',
        sendInvitation: false,
        autoGeneratePassword: false,
      },
    ];

    setTimeout(() => {
      demoMembers.forEach(member => {
        this.createMember(member, 'system').then(created => {
          // Activate the user
          this.updateMember(created.id, { status: 'active' }, 'system');
          
          // Simulate some activity
          const activeMember = this.members.get(created.id);
          if (activeMember) {
            activeMember.lastLogin = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString();
            activeMember.lastActivity = new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString();
            activeMember.loginCount = Math.floor(Math.random() * 50) + 10;
            this.members.set(created.id, activeMember);
          }
        }).catch(console.error);
      });
    }, 100);
  }
}

// Singleton instance
export const teamService = new TeamService();
